// Portfolios registry at /portfolio-admin/portfolios — the lightweight
// management surface (create / rename / describe / archive). Creation is
// ALSO available inline from the "Move to portfolio" picker; this page is
// the home for rollups + archive. Names link into the portfolio dashboard.

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import {
  usePortfolios,
  useCreatePortfolio,
  useUpdatePortfolio,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  Input,
  OverflowMenu,
  StatusBadge,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import type { PortfolioListItemDto } from "@/api/portfolioAdmin/types";

export default function PortfoliosListPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Portfolios"
        description="Named loan groupings with rollups."
      />
    );
  }
  return <PortfoliosListPageInner />;
}

function PortfoliosListPageInner() {
  const { tz } = useDateFormatter();
  const auth = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const portfolios = usePortfolios();
  const update = useUpdatePortfolio();
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PortfolioListItemDto | null>(null);

  const canManage =
    auth.hasRoleCode("ADMIN") || auth.hasPermission("Loans.Manage");

  const rows = useMemo(
    () =>
      (portfolios.data ?? []).filter(
        (p) => showArchived || p.status === "Open",
      ),
    [portfolios.data, showArchived],
  );

  const columns: Column<PortfolioListItemDto>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      sortAccessor: (p) => p.name,
      render: (p) => (
        <span className="text-gold-shadow hover:underline font-semibold">
          {p.name}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (p) => (
        <StatusBadge tone={p.status === "Open" ? "ok" : "neutral"}>
          {p.status}
        </StatusBadge>
      ),
    },
    {
      key: "loans",
      header: "Loans",
      className: "text-right tabular-nums",
      sortable: true,
      sortAccessor: (p) => p.loanCount,
      render: (p) => p.loanCount,
    },
    {
      key: "balance",
      header: "Total balance",
      className: "text-right tabular-nums",
      sortable: true,
      sortAccessor: (p) => p.totalCurrentBalance,
      render: (p) => formatCurrency(p.totalCurrentBalance),
    },
    {
      key: "created",
      header: "Created",
      sortable: true,
      sortAccessor: (p) => new Date(p.createdAt),
      render: (p) => (
        <span className="text-small text-gray-mid tabular-nums">
          {formatDateInTz(p.createdAt, tz)}
        </span>
      ),
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "",
            render: (p: PortfolioListItemDto) => (
              <span onClick={(e) => e.stopPropagation()}>
                <OverflowMenu
                  label={`Actions for ${p.name}`}
                  data-testid={`portfolio-actions-${p.id}`}
                  items={[
                    { label: "Edit", onSelect: () => setEditing(p) },
                    p.status === "Open"
                      ? {
                          label: "Archive",
                          destructive: true,
                          confirm: "Archive this portfolio? Its loans keep their assignment.",
                          onSelect: () =>
                            void update
                              .mutateAsync({ id: p.id, body: { status: "Archived" } })
                              .then(() => toast.success(`"${p.name}" archived.`))
                              .catch((e: Error) => toast.error(e.message)),
                        }
                      : {
                          label: "Unarchive",
                          onSelect: () =>
                            void update
                              .mutateAsync({ id: p.id, body: { status: "Open" } })
                              .then(() => toast.success(`"${p.name}" reopened.`))
                              .catch((e: Error) => toast.error(e.message)),
                        },
                  ]}
                />
              </span>
            ),
          } satisfies Column<PortfolioListItemDto>,
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-space-3" data-testid="portfolios-list-page">
      <div className="flex items-center justify-between gap-space-3">
        <h1 className="font-display font-bold text-h1 m-0">Portfolios</h1>
        {canManage && (
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="portfolios-add">
            + New portfolio
          </Button>
        )}
      </div>

      <label className="inline-flex items-center gap-space-1 text-small text-onyx cursor-pointer self-start">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          data-testid="portfolios-show-archived"
        />
        Show archived
      </label>

      <DataTable<PortfolioListItemDto>
        columns={columns}
        rows={rows}
        loading={portfolios.isLoading}
        error={portfolios.isError ? "Couldn't load portfolios. Try again." : null}
        emptyMessage='No portfolios yet. Create one here or inline from any "Move to portfolio" picker.'
        rowKey={(p) => p.id}
        sortable
        pagination="auto"
        onRowClick={(p) => navigate(`/portfolio-admin/portfolios/${p.id}`)}
        rowTestId={(p) => `portfolios-row-${p.id}`}
        data-testid="portfolios-table"
      />

      {createOpen && (
        <PortfolioEditModal
          onClose={() => setCreateOpen(false)}
          onSaved={(name) => {
            setCreateOpen(false);
            toast.success(`Portfolio "${name}" created.`);
          }}
        />
      )}
      {editing && (
        <PortfolioEditModal
          portfolio={editing}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null);
            toast.success(`Portfolio "${name}" updated.`);
          }}
        />
      )}
    </div>
  );
}

function PortfolioEditModal({
  portfolio,
  onClose,
  onSaved,
}: {
  portfolio?: PortfolioListItemDto;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const [name, setName] = useState(portfolio?.name ?? "");
  const [description, setDescription] = useState(portfolio?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const create = useCreatePortfolio();
  const update = useUpdatePortfolio();
  const saving = create.isPending || update.isPending;

  async function save() {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    try {
      if (portfolio) {
        await update.mutateAsync({
          id: portfolio.id,
          body: { name: trimmed, description },
        });
      } else {
        await create.mutateAsync({ name: trimmed, description: description || null });
      }
      onSaved(trimmed);
    } catch (e) {
      setError((e as Error).message ?? "Save failed.");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={portfolio ? "Edit portfolio" : "New portfolio"}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={saving}
            disabled={!name.trim() || saving}
            onClick={() => void save()}
            data-testid="portfolio-save"
          >
            {portfolio ? "Save" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3" data-testid="portfolio-edit-modal">
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">Name</span>
          <Input
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            data-testid="portfolio-name-input"
          />
        </div>
        <div className="flex flex-col gap-space-1">
          <span className="text-caption text-gray-mid uppercase tracking-label">
            Description (optional)
          </span>
          <Textarea
            value={description}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {error && (
          <p className="text-label text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
